import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CheckCircle2, AlertTriangle, XCircle, Building2, FileText, 
  ChevronDown, ChevronUp, Send, Loader2, Target, Shield, Cog, 
  Truck, Coins, Check
} from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_ICONS = {
  functional: <Target className="w-4 h-4" />,
  non_functional: <Cog className="w-4 h-4" />,
  integration: <Cog className="w-4 h-4" />,
  security: <Shield className="w-4 h-4" />,
  migration: <Truck className="w-4 h-4" />,
  cost: <Coins className="w-4 h-4" />
};

const CATEGORY_NAMES = {
  functional: 'Funktional',
  non_functional: 'Nicht-funktional',
  integration: 'Integration',
  security: 'Sicherheit',
  migration: 'Migration',
  cost: 'Kosten'
};

const PRIORITY_NAMES = {
  must: 'Muss',
  should: 'Soll',
  could: 'Kann',
  wont: 'Nicht'
};

const PRIORITY_COLORS = {
  must: 'bg-red-100 text-red-800 border-red-200',
  should: 'bg-amber-100 text-amber-800 border-amber-200',
  could: 'bg-blue-100 text-blue-800 border-blue-200',
  wont: 'bg-gray-100 text-gray-800 border-gray-200'
};

const FULFILLMENT_OPTIONS = [
  { value: 'fulfilled', label: 'Erfüllt', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50 border-green-200' },
  { value: 'partial', label: 'Teilweise erfüllt', icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  { value: 'not_fulfilled', label: 'Nicht erfüllt', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50 border-red-200' }
];

const VendorPortalPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchPortalData();
  }, [token]);

  const fetchPortalData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/requirements-catalog/portal/${token}`);
      setData(response.data);
      setResponses(response.data.responses || {});
    } catch (err) {
      setError(err.response?.data?.detail || 'Link ungültig oder abgelaufen');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (reqId) => {
    setExpandedItems(prev => ({ ...prev, [reqId]: !prev[reqId] }));
  };

  const updateResponse = (reqId, field, value) => {
    setResponses(prev => ({
      ...prev,
      [reqId]: {
        ...prev[reqId],
        [field]: value
      }
    }));
  };

  const saveResponse = async (reqId) => {
    const response = responses[reqId];
    if (!response?.fulfillment_status) {
      toast.error('Bitte wählen Sie einen Erfüllungsgrad');
      return;
    }

    setSaving(prev => ({ ...prev, [reqId]: true }));
    try {
      await axios.post(`${API}/requirements-catalog/portal/${token}/response`, {
        requirement_id: reqId,
        fulfillment_status: response.fulfillment_status,
        comment: response.comment || '',
        reference_project: response.reference_project || ''
      });
      toast.success('Antwort gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(prev => ({ ...prev, [reqId]: false }));
    }
  };

  const submitAssessment = async () => {
    const unanswered = data.requirements.filter(r => !responses[r.id]?.fulfillment_status);
    if (unanswered.length > 0) {
      if (!window.confirm(`Es sind noch ${unanswered.length} Anforderungen unbeantwortet. Trotzdem einreichen?`)) {
        return;
      }
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/requirements-catalog/portal/${token}/submit`);
      toast.success('Selbstauskunft erfolgreich eingereicht!');
      fetchPortalData();
    } catch (err) {
      toast.error('Fehler beim Einreichen');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Lade Anforderungskatalog...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 mx-auto text-destructive mb-4" />
            <h2 className="text-xl font-semibold mb-2">Zugriff nicht möglich</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const answeredCount = Object.keys(responses).filter(k => responses[k]?.fulfillment_status).length;
  const progress = Math.round((answeredCount / data.total_requirements) * 100);

  const filteredRequirements = data.requirements.filter(req => {
    if (filterCategory !== 'all' && req.category !== filterCategory) return false;
    if (filterStatus === 'answered' && !responses[req.id]?.fulfillment_status) return false;
    if (filterStatus === 'unanswered' && responses[req.id]?.fulfillment_status) return false;
    return true;
  });

  // Group requirements by category
  const groupedRequirements = filteredRequirements.reduce((acc, req) => {
    if (!acc[req.category]) acc[req.category] = [];
    acc[req.category].push(req);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {data.vendor.logo_url ? (
                <img src={data.vendor.logo_url} alt={data.vendor.name} className="w-12 h-12 object-contain rounded border bg-white p-1" />
              ) : (
                <div className="w-12 h-12 rounded border bg-muted flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
              <div>
                <h1 className="font-semibold text-lg">{data.vendor.name}</h1>
                <p className="text-sm text-muted-foreground">Selbstauskunft für: {data.catalog.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Fortschritt</div>
                <div className="font-semibold">{answeredCount} / {data.total_requirements}</div>
              </div>
              <div className="w-32">
                <Progress value={progress} className="h-2" />
              </div>
              <Button onClick={submitAssessment} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Einreichen
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Anforderungskatalog: {data.catalog.name}</CardTitle>
            <CardDescription>
              {data.catalog.description || `Bitte beantworten Sie die folgenden ${data.total_requirements} Anforderungen.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Erfüllt = Funktion ist vorhanden</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span>Teilweise = Mit Einschränkungen</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-600" />
                <span>Nicht erfüllt = Nicht vorhanden</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Kategorie" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Kategorien</SelectItem>
              {Object.entries(CATEGORY_NAMES).map(([key, name]) => (
                <SelectItem key={key} value={key}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle</SelectItem>
              <SelectItem value="answered">Beantwortet</SelectItem>
              <SelectItem value="unanswered">Unbeantwortet</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground self-center">
            {filteredRequirements.length} Anforderungen angezeigt
          </div>
        </div>

        {/* Requirements by Category */}
        <ScrollArea className="h-[calc(100vh-320px)]">
          <div className="space-y-6">
            {Object.entries(groupedRequirements).map(([category, reqs]) => (
              <div key={category}>
                <div className="flex items-center gap-2 mb-3 sticky top-0 bg-gradient-to-br from-slate-50 to-slate-100 py-2">
                  <span className="text-primary">{CATEGORY_ICONS[category]}</span>
                  <h2 className="font-semibold">{CATEGORY_NAMES[category]}</h2>
                  <Badge variant="outline">{reqs.length}</Badge>
                </div>
                <div className="space-y-2">
                  {reqs.map((req) => {
                    const response = responses[req.id] || {};
                    const isExpanded = expandedItems[req.id];
                    const fulfillment = FULFILLMENT_OPTIONS.find(f => f.value === response.fulfillment_status);

                    return (
                      <Card 
                        key={req.id} 
                        className={`transition-all ${fulfillment ? fulfillment.bg : ''} ${isExpanded ? 'ring-2 ring-primary/20' : ''}`}
                      >
                        <CardContent className="p-0">
                          {/* Header Row */}
                          <div 
                            className="p-4 flex items-center gap-3 cursor-pointer"
                            onClick={() => toggleExpanded(req.id)}
                          >
                            <div className="text-muted-foreground">
                              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                            </div>
                            
                            {/* Status Icon */}
                            {fulfillment ? (
                              <fulfillment.icon className={`w-5 h-5 ${fulfillment.color}`} />
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-dashed border-muted-foreground/30" />
                            )}

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-xs text-muted-foreground">{req.display_id}</span>
                                <h4 className="font-medium">{req.title}</h4>
                                <Badge variant="outline" className={PRIORITY_COLORS[req.priority]}>
                                  {PRIORITY_NAMES[req.priority]}
                                </Badge>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1 truncate">{req.short_description}</p>
                            </div>

                            {response.fulfillment_status && (
                              <Badge className={fulfillment?.bg}>
                                {fulfillment?.label}
                              </Badge>
                            )}
                          </div>

                          {/* Expanded Form */}
                          {isExpanded && (
                            <div className="px-4 pb-4 border-t bg-white/50">
                              <div className="pt-4 space-y-4">
                                {/* Description */}
                                {req.topic_explanation && (
                                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm">
                                    <Label className="text-xs text-blue-600 uppercase font-semibold mb-1 block">Erklärung</Label>
                                    <p className="text-blue-900">{req.short_description}</p>
                                  </div>
                                )}

                                {/* Fulfillment Selection */}
                                <div>
                                  <Label className="text-xs text-muted-foreground uppercase mb-2 block">Erfüllungsgrad *</Label>
                                  <div className="grid grid-cols-3 gap-2">
                                    {FULFILLMENT_OPTIONS.map((option) => {
                                      const Icon = option.icon;
                                      const isSelected = response.fulfillment_status === option.value;
                                      return (
                                        <Button
                                          key={option.value}
                                          variant={isSelected ? "default" : "outline"}
                                          className={`justify-start ${isSelected ? '' : 'hover:' + option.bg}`}
                                          onClick={() => updateResponse(req.id, 'fulfillment_status', option.value)}
                                        >
                                          <Icon className={`w-4 h-4 mr-2 ${isSelected ? '' : option.color}`} />
                                          {option.label}
                                        </Button>
                                      );
                                    })}
                                  </div>
                                </div>

                                {/* Comment */}
                                <div>
                                  <Label className="text-xs text-muted-foreground uppercase mb-2 block">Kommentar / Wie wird es erfüllt?</Label>
                                  <Textarea
                                    value={response.comment || ''}
                                    onChange={(e) => updateResponse(req.id, 'comment', e.target.value)}
                                    placeholder="Beschreiben Sie, wie diese Anforderung erfüllt wird..."
                                    rows={3}
                                  />
                                </div>

                                {/* Reference Project */}
                                <div>
                                  <Label className="text-xs text-muted-foreground uppercase mb-2 block">Referenzprojekt (optional)</Label>
                                  <Input
                                    value={response.reference_project || ''}
                                    onChange={(e) => updateResponse(req.id, 'reference_project', e.target.value)}
                                    placeholder="z.B. Kunde XY, Projekt ABC"
                                  />
                                </div>

                                {/* Save Button */}
                                <div className="flex justify-end">
                                  <Button 
                                    onClick={() => saveResponse(req.id)}
                                    disabled={saving[req.id] || !response.fulfillment_status}
                                  >
                                    {saving[req.id] ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <Check className="w-4 h-4 mr-2" />
                                    )}
                                    Speichern
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default VendorPortalPage;
